/**
 * Invitation data fetching and manipulation hooks.
 *
 * Both `invitation` and `invite` are used here, but `invitation` should be
 * preferred for use in arguments and as a key within objects.
 *
 * @category data
 */

import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { api, ApiResponse } from "_/utils";
import { Uuid } from "_/types";

import { Org, KEYS as ORG_KEYS } from "_/data/orgs";
import { Role } from "_/data/rbac";

const SCOPE = "invitations";
const KEYS = {
  all: () => [SCOPE],
  org: (orgId: Uuid | undefined) => [SCOPE, "org", orgId],
  user: () => [SCOPE, "user"],
};

/**
 * Invitation for new users to join an organization within Basis and optionally create an account if they do not yet have one.
 */
export type Invitation = {
  /**
   * Invitation ID.
   */
  id: Uuid;

  /**
   * Organization the user is being invited to.
   */
  organizationId: Uuid;

  /**
   * User role to be assigned upon invitation completion.
   */
  role: Role;

  /**
   * Email the invitation is sent to.
   */
  email: string;

  /**
   * User that initiated the invitation.
   */
  invitedBy: Uuid;

  /**
   * Flag indicating if the invitation has been approved.
   */
  approved: boolean;

  /**
   * Flag indicating if the invitation has been accepted.
   */
  accepted: boolean;

  /**
   * User that approved or denied the invitation.
   */
  reviewedBy: Uuid;

  /**
   * User that accepted or rejected the invitation.
   */
  receivedBy: string;

  /**
   * If set, the user that revoked the invitation
   */
  revokedBy: Uuid | null;

  /**
   * Date and time the invitation was created.
   */
  createdAt: string;

  /**
   * Date and time the invitation was last modified.
   *
   * This will be updated when the invitation is approved, denied, accepted, resent, or revoked.
   */
  updatedAt: string;

  /**
   * Expiration date and time for the invitation.
   */
  expiresAt: string;
};

export type InvitationWithDetail = Invitation & {
  organizationName: string;
  inviterName: string;
};

/**
 * Invitation as returned by the endpoints that send it.
 *
 * On installs without email, `inviteLink` is the link the new user needs to
 * accept the invitation, for the admin to hand over directly.
 */
export type InvitationResponse = Invitation & {
  inviteLink?: string;
};

export function useInviteUser() {
  const queryClient = useQueryClient();

  async function requestInvite({
    email,
    role,
    org,
  }: {
    email: string;
    role: Role;
    org: Org;
  }) {
    const response = await api.post<ApiResponse<InvitationResponse>>(
      "/invitations",
      {
        email,
        role,
        orgId: org.id,
      }
    );

    return response.data.data;
  }

  return useMutation({
    mutationFn: requestInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
    },
  });
}

export function useInviteMutations() {
  const queryClient = useQueryClient();

  async function resendInvite(params: {
    invitationId: Uuid;
  }): Promise<InvitationResponse> {
    const response = await api.post<ApiResponse<InvitationResponse>>(
      `/invitations/${params.invitationId}/resend`
    );
    return response.data.data;
  }

  async function revokeInvite(params: {
    invitationId: Uuid;
  }): Promise<Invitation> {
    const response = await api.delete<ApiResponse<Invitation>>(
      `/invitations/${params.invitationId}`
    );
    return response.data.data;
  }

  async function reviewInvite(params: {
    invitationId: Uuid;
    approval: boolean;
  }): Promise<InvitationResponse> {
    const response = await api.post<ApiResponse<InvitationResponse>>(
      `/invitations/${params.invitationId}/review`,
      {
        approval: params.approval,
      }
    );
    return response.data.data;
  }

  async function receiveInvite(params: {
    invitationId: Uuid;
    acceptance: boolean;
  }): Promise<Invitation> {
    const response = await api.post<ApiResponse<Invitation>>(
      `/invitations/${params.invitationId}/receive`,
      {
        acceptance: params.acceptance,
      }
    );
    return response.data.data;
  }

  const onSuccess = (data: InvitationResponse) => {
    // Keep the one-time invite link out of the query cache; only the caller
    // that triggered the request should see it.
    const { inviteLink: _inviteLink, ...invitation } = data;

    const updateData = (oldData: Invitation[] | undefined): Invitation[] => {
      if (!oldData) {
        return [invitation];
      }

      return oldData.map((item) => {
        return item.id === invitation.id ? invitation : item;
      });
    };

    queryClient.setQueryData(KEYS.org(invitation.organizationId), updateData);

    // The cached data above doesn't overlap with the keys we're invalidating
    // here, so no race condition is possible and so fire-and-forget is OK.
    void queryClient.invalidateQueries({ queryKey: ORG_KEYS.current() });
    void queryClient.invalidateQueries({ queryKey: KEYS.user() });
  };

  return {
    resendInvite: useMutation({ mutationFn: resendInvite }),
    revokeInvite: useMutation({ mutationFn: revokeInvite, onSuccess }),
    reviewInvite: useMutation({ mutationFn: reviewInvite, onSuccess }),
    receiveInvite: useMutation({ mutationFn: receiveInvite, onSuccess }),
  };
}

async function getOrgInvitations(orgId: Uuid) {
  const response = await api.get<ApiResponse<InvitationWithDetail[]>>(
    `/organizations/${orgId}/invitations`
  );
  return response.data.data;
}

export function useOrgInvites(orgId: Uuid) {
  const config = {
    queryKey: KEYS.org(orgId),
    queryFn: () => getOrgInvitations(orgId),
  };

  return useQuery(config);
}

export function useOrgInvitesCount(orgId: Uuid | undefined) {
  return useQuery({
    queryKey: KEYS.org(orgId),
    queryFn: orgId ? () => getOrgInvitations(orgId) : skipToken,
    select: (data: InvitationWithDetail[]) =>
      data.filter(isInvitePending).length,
  });
}

export function useUserInvites() {
  async function getUserInvitations() {
    const response = await api.get<ApiResponse<InvitationWithDetail[]>>(
      `/users/me/invitations`
    );
    return response.data.data;
  }

  const config = {
    queryKey: KEYS.user(),
    queryFn: getUserInvitations,
  };

  return useQuery(config);
}

export function isInvitePending(i: Invitation): boolean {
  return !i.revokedBy && !i.receivedBy && new Date(i.expiresAt) > new Date();
}
